import { Box, alpha, useTheme } from "@mui/material";
import { minimoDoDia, type DiaColuna, type LinhaCarga, type TotaisDia } from "@/components/escala/espelho";
import { corDaJornada } from "@/components/escala/cargaHoraria";
import {
  ALTURA_TOTAIS,
  celulaBase,
  faixa,
  fixaDireita,
  fixaEsquerda,
  larguraColaborador,
  rotuloCaixaAlta,
} from "@/components/escala/espelhoEstilos";

const TOTAIS = [
  { rotulo: "Trabalhando", chave: "trabalhando", checaMinimo: true },
  { rotulo: "Folgando", chave: "folgando", checaMinimo: false },
  { rotulo: "Compensação", chave: "compensando", checaMinimo: false },
] as const;

interface EspelhoRodapeProps {
  colunas: DiaColuna[];
  // Quantas colunas do mês anterior a grade tem antes do dia 1 — só espaçamento aqui.
  colunasHistorico?: number;
  // Quantas colunas de totais à direita (TT, TF e, com o filtro de domingos, DT).
  colunasTotais?: number;
  porDia: TotaisDia[];
  // Uma linha por carga aplicada no mês, depois dos totais, com os valores na ordem de colunas.
  cargas?: LinhaCarga[];
  minimo: number;
  // Mínimo do domingo, quando a geração informou; sem ele, o domingo usa o de segunda a sábado.
  minimoDomingo?: number;
}

interface Linha {
  chave: string;
  rotulo: string;
  valores: number[];
  checaMinimo: boolean;
  daCarga: boolean;
  // Cor da carga, já ajustada ao fundo do rodapé; sem ela, a cor normal.
  cor?: string;
}

// O nome da carga não vai em caixa alta, para ser lido como foi cadastrado.
const nomeDaCarga = {
  textTransform: "none",
  letterSpacing: 0,
  fontSize: 12,
  maxWidth: larguraColaborador(),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

// Preso na base: os totais ficam visíveis com a grade rolada.
export function EspelhoRodape({
  colunas,
  colunasHistorico = 0,
  colunasTotais = 2,
  porDia,
  cargas = [],
  minimo,
  minimoDomingo,
}: EspelhoRodapeProps) {
  const theme = useTheme();
  const linhas: Linha[] = [
    ...TOTAIS.map((t) => ({
      chave: t.chave,
      rotulo: t.rotulo,
      valores: porDia.map((d) => d[t.chave]),
      checaMinimo: t.checaMinimo,
      daCarga: false,
    })),
    ...cargas.map((c) => ({
      chave: `carga-${c.id}`,
      rotulo: c.descricao,
      valores: c.porDia,
      checaMinimo: false,
      daCarga: true,
      cor: corDaJornada(c.cor, theme.palette.background.subtle),
    })),
  ];
  return (
    <tfoot>
      {linhas.map((linha, i) => {
        const rodape = {
          position: "sticky",
          bottom: (linhas.length - 1 - i) * ALTURA_TOTAIS,
          zIndex: 3,
          height: ALTURA_TOTAIS,
          bgcolor: "background.subtle",
          borderTop: i === 0 ? "2px solid" : "none",
          borderTopColor: "divider",
        } as const;
        return (
          <Box component="tr" key={linha.chave}>
            <Box
              component="td"
              title={linha.daCarga ? linha.rotulo : undefined}
              sx={{
                ...celulaBase,
                ...fixaEsquerda,
                ...rodape,
                zIndex: 5,
                pl: 2.5,
                textAlign: "left",
                ...rotuloCaixaAlta,
                ...(linha.daCarga && nomeDaCarga),
              }}
              style={linha.cor ? { color: linha.cor } : undefined}
            >
              {linha.rotulo}
            </Box>
            {colunasHistorico > 0 && <Box component="td" colSpan={colunasHistorico} sx={[celulaBase, rodape]} />}
            {linha.valores.map((valor, idx) => {
              const coluna = colunas[idx]!;
              // Feriado com loja fechada: ninguém trabalha mesmo, então "abaixo do mínimo" não
              // é ocorrência nesse dia (R51 — sem isso o rodapé fica vermelho todo feriado).
              const minimoDaColuna = minimoDoDia(coluna, minimo, minimoDomingo);
              const abaixo = linha.checaMinimo && valor < minimoDaColuna && !coluna.fechaLoja;
              return (
                <Box
                  component="td"
                  key={coluna.dia}
                  title={abaixo ? `Abaixo do mínimo (${minimoDaColuna})` : undefined}
                  sx={[celulaBase, rodape, faixa(coluna), { fontWeight: 600 }]}
                >
                  {abaixo ? (
                    <Box
                      sx={(theme) => ({
                        mx: "auto",
                        width: 26,
                        height: 22,
                        lineHeight: "22px",
                        borderRadius: 1,
                        fontWeight: 800,
                        bgcolor: alpha(theme.palette.error.main, 0.14),
                        color: "error.main",
                      })}
                    >
                      {valor}
                    </Box>
                  ) : (
                    <Box sx={{ color: "text.secondary" }} style={linha.cor ? { color: linha.cor } : undefined}>
                      {valor}
                    </Box>
                  )}
                </Box>
              );
            })}
            <Box component="td" colSpan={colunasTotais} sx={{ ...celulaBase, ...rodape, ...fixaDireita(0), zIndex: 5 }} />
          </Box>
        );
      })}
    </tfoot>
  );
}
